import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { colors, typography } from '../constants';

/**
 * Adds thousands separators to a numeric string, preserving any decimals the
 * user typed (e.g. "5681.46" -> "5,681.46"). Non-numeric strings pass through.
 */
function addThousands(s) {
  const n = Number(s);
  if (s === '' || isNaN(n)) return s;
  const [int, dec] = s.split('.');
  const grouped = Number(int).toLocaleString('en-US');
  return dec !== undefined ? `${grouped}.${dec}` : grouped;
}

/**
 * Commit-on-blur numeric input. Separators are shown while the field is idle
 * and stripped while it is focused, so what the owner types stays parseable.
 */
export function NumberField({
  initialValue,
  onCommit,
  width = 56,
  fontSize = 16,
  color,
  underlineColor,
  align = 'center',
  disabled = false,
  onFocus,
}) {
  const resolvedColor = color ?? colors.primary;
  const resolvedUnderlineColor = underlineColor ?? colors.primary;
  const [val, setVal] = useState(String(initialValue));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setVal(String(initialValue));
  }, [initialValue]);

  const display = focused ? val : addThousands(val);
  // Auto-grow so long values (e.g. "5,681.46") never get clipped.
  const fitWidth = Math.max(width, display.length * fontSize * 0.62 + 10);

  return (
    <Box
      component="input"
      type="text"
      inputMode="decimal"
      disabled={disabled}
      value={display}
      onChange={(e) => setVal(e.target.value.replace(/,/g, ''))}
      onFocus={(e) => {
        setFocused(true);
        e.target.select();
        onFocus?.();
      }}
      onBlur={() => {
        setFocused(false);
        // A field left empty reads as zero. Committing "" would be turned away
        // by every parser upstream, which would leave the figure that was just
        // cleared sitting back in the field.
        const raw = val.trim() === '' ? '0' : val;
        setVal(raw);
        onCommit(raw);
      }}
      sx={{
        width: fitWidth,
        py: '2px',
        px: '4px',
        fontSize,
        fontWeight: 800,
        fontFamily: 'inherit',
        border: 'none',
        borderBottom: `2px solid ${
          focused ? colors.primary : resolvedUnderlineColor
        }`,
        backgroundColor: 'transparent',
        color: resolvedColor,
        textAlign: align,
        outline: 'none',
        '&:disabled': { color: colors.mutedForeground, cursor: 'not-allowed' },
      }}
    />
  );
}

/** Plain bordered text input used by forms and cost line names. */
export function TextField({ initialValue, onCommit, placeholder, sx }) {
  const [val, setVal] = useState(initialValue ?? '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setVal(initialValue ?? '');
  }, [initialValue]);

  return (
    <Box
      component="input"
      type="text"
      value={val}
      placeholder={placeholder}
      onChange={(e) => setVal(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        onCommit(val);
      }}
      sx={{
        px: 1.25,
        py: 0.75,
        fontSize: typography.small,
        fontFamily: 'inherit',
        color: colors.foreground,
        borderRadius: 1.5,
        border: `1px solid ${focused ? colors.primary : colors.border}`,
        boxShadow: focused ? `0 0 0 3px ${colors.primary}33` : 'none',
        outline: 'none',
        '&::placeholder': { color: colors.faint },
        ...sx,
      }}
    />
  );
}

/** Segmented two-or-more-way switch, e.g. percent vs dollar. */
export function Toggle({ value, onChange, options, sx }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        backgroundColor: colors.muted,
        borderRadius: 2,
        p: '3px',
        gap: '2px',
        ...sx,
      }}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <Box
            component="button"
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            sx={{
              py: 0.5,
              px: 1.25,
              borderRadius: 1.5,
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: typography.caption,
              fontWeight: 600,
              backgroundColor: active ? colors.primary : 'transparent',
              color: active ? colors.primaryForeground : colors.mutedForeground,
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}

/** Rounded filter chip used by the date bar and sub-navigation. */
export function Pill({ active, label, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        py: 0.875,
        px: 1.75,
        borderRadius: 999,
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: typography.caption,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        backgroundColor: active ? colors.primary : colors.muted,
        color: active ? colors.primaryForeground : colors.mutedForeground,
      }}
    >
      {label}
    </Box>
  );
}
